/**
 * ProctorWrapper (Sprint 6 B5) — compose all proctoring components.
 *
 * Usage: wrap QuizTake content khi muốn enable proctoring.
 *
 * Props:
 *   - sessionId: ProctorSession.id (từ attempt.proctor_session)
 *   - webcamRequired: từ Quiz.webcam_required (per-quiz config)
 *
 * Side effects:
 *   - FullscreenLock — request fullscreen + log fullscreen_exit
 *   - TabSwitchDetector — log tab_switch
 *   - PasteCopyBlocker — block paste/copy + log
 *   - WebcamCapture — stream + snapshot 30s nếu required=true
 *   - useViolationQueue — buffer + flush violations 5s batch
 */

import { useCallback } from "react";
import { FullscreenLock } from "./FullscreenLock";
import { PasteCopyBlocker } from "./PasteCopyBlocker";
import { TabSwitchDetector } from "./TabSwitchDetector";
import { WebcamCapture } from "./WebcamCapture";
import { useViolationQueue } from "./useViolationQueue";
import type { ViolationEvent } from "@/api/proctoring";

interface Props {
  sessionId: number;
  webcamRequired: boolean;
  children: React.ReactNode;
}

export function ProctorWrapper({ sessionId, webcamRequired, children }: Props) {
  const { enqueue } = useViolationQueue(sessionId);

  const handle = useCallback(
    (event: Omit<ViolationEvent, "occurred_at">) => {
      enqueue(event);
    },
    [enqueue],
  );

  return (
    <>
      <FullscreenLock onViolation={handle} />
      <TabSwitchDetector onViolation={handle} />
      <PasteCopyBlocker onViolation={handle} />
      <WebcamCapture
        sessionId={sessionId}
        required={webcamRequired}
        onViolation={handle}
      />
      {children}
    </>
  );
}
